import { useState, useEffect, useCallback, useRef } from "react";
import { fetcher } from "../../../utils/fetcher";
import FlowTextInput from "../../FlowTextInput/FlowTextInput";
import ToolTip from "../../ToolTip/ToolTip";
import Spinner from "../../Spinner/Spinner";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import "./ListEditorModal.scss";

// Sortable Item Component
function SortableListItem({ item, index, color, onToggle, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item._id || `item-${index}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? "none" : transition,
    opacity: isDragging ? 0.5 : 1,
    borderLeftColor: color,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`list-item ${item.completed ? "completed" : ""}`}
      onClick={(e) => e.stopPropagation()}
    >
      <input
        type="checkbox"
        className="item-checkbox"
        checked={item.completed}
        onChange={() => onToggle(index)}
      />
      <div className="item-content">
        <span className="item-text">{item.text}</span>
        {item.description && (
          <span className="item-description">{item.description}</span>
        )}
      </div>
      {item.deadline && (
        <span className="item-deadline">
          <span className="material-symbols-outlined">calendar_month</span>
          {new Date(item.deadline).toLocaleDateString("da-DK", {
            day: "numeric",
            month: "short",
          })}
        </span>
      )}
      <button className="item-delete-btn" onClick={() => onDelete(index)}>
        <span className="material-symbols-outlined">delete</span>
      </button>
      <div
        className="drag-handle"
        {...attributes}
        {...listeners}
        style={{ touchAction: "none" }}
      >
        <span className="material-symbols-outlined">drag_indicator</span>
      </div>
    </div>
  );
}

function ListEditorModal({ listId, onListUpdated }) {
  const [list, setList] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [newItemText, setNewItemText] = useState("");
  const [addToTop, setAddToTop] = useState(false);
  const toolTipRef = useRef();
  const newItemInputRef = useRef();

  // Configure sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  // Fetch the list data
  useEffect(() => {
    if (listId) {
      setIsLoading(true);
      fetcher
        .get(`/api/todolists/${listId}`)
        .then((data) => {
          setList(data);
          // Load addToTop preference from localStorage
          const storageKey = `todolist-addToTop-${data._id}`;
          const savedPref = localStorage.getItem(storageKey);
          if (savedPref !== null) {
            setAddToTop(JSON.parse(savedPref));
          }
          setIsLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching list:", err);
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
    }
  }, [listId]);

  // Save list to server
  const saveList = useCallback(
    async (updatedList) => {
      if (!updatedList) return;

      try {
        const updatedData = await fetcher.put(
          `/api/todolists/${updatedList._id}`,
          {
            title: updatedList.title,
            items: updatedList.items,
          },
        );

        if (onListUpdated) {
          onListUpdated(updatedData);
        }

        return updatedData;
      } catch (err) {
        console.error("Error updating list:", err);
      }
    },
    [onListUpdated],
  );

  const handleToggleAddPosition = (toTop) => {
    if (!list) return;
    setAddToTop(toTop);
    const storageKey = `todolist-addToTop-${list._id}`;
    localStorage.setItem(storageKey, JSON.stringify(toTop));
  };

  const showToolTip = (e, content) => {
    const triggerElement = e.currentTarget;
    if (toolTipRef.current) {
      toolTipRef.current.open({
        arrow: "top-right",
        content,
        triggerElement,
      });
    }
  };

  const handleAddItem = async () => {
    if (!list || !newItemText.trim()) return;

    let updatedItems;

    if (addToTop) {
      // Add to top: new item at index 0, shift others down
      const newItem = {
        text: newItemText.trim(),
        completed: false,
        sortOrder: 0,
      };
      updatedItems = [
        newItem,
        ...list.items.map((item, index) => ({
          ...item,
          sortOrder: index + 1,
        })),
      ];
    } else {
      // Add to bottom: new item at the end
      const newItem = {
        text: newItemText.trim(),
        completed: false,
        sortOrder: list.items.length,
      };
      updatedItems = [...list.items, newItem];
    }

    const updatedList = { ...list, items: updatedItems };
    setList(updatedList);
    setNewItemText("");

    // Save to server
    const savedList = await saveList(updatedList);
    if (savedList) {
      setList(savedList);
    }
  };

  const handleToggleItem = async (index) => {
    if (!list) return;

    const updatedItems = list.items.map((item, i) =>
      i === index ? { ...item, completed: !item.completed } : item,
    );
    const updatedList = { ...list, items: updatedItems };
    setList(updatedList);

    // Save to server
    await saveList(updatedList);
  };

  const handleDeleteItem = async (index) => {
    if (!list) return;

    const updatedItems = list.items.filter((_, i) => i !== index);
    const updatedList = { ...list, items: updatedItems };
    setList(updatedList);

    // Save to server
    await saveList(updatedList);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id || !list) {
      return;
    }

    const oldIndex = list.items.findIndex(
      (item, idx) => (item._id || `item-${idx}`) === active.id,
    );
    const newIndex = list.items.findIndex(
      (item, idx) => (item._id || `item-${idx}`) === over.id,
    );

    const newItems = arrayMove(list.items, oldIndex, newIndex).map(
      (item, index) => ({
        ...item,
        sortOrder: index,
      }),
    );

    // Optimistically update the UI first
    const updatedList = { ...list, items: newItems };
    setList(updatedList);

    // Save to server
    const savedList = await saveList(updatedList);
    if (savedList) {
      setList(savedList);
    }
  };

  if (isLoading) {
    return (
      <div className="list-editor-modal">
        <Spinner />
      </div>
    );
  }

  if (!list) {
    return (
      <div className="list-editor-modal">
        <div className="empty-state">List not found</div>
      </div>
    );
  }

  const completedCount = list.items.filter((item) => item.completed).length;
  const totalCount = list.items.length;

  return (
    <div className="list-editor-modal">
      <ToolTip ref={toolTipRef} />

      <div className="list-stats">
        <span
          className={`list-type-badge ${list.type === "checklist" ? "type-checklist" : "type-todolist"}`}
        >
          {list.type === "checklist" ? "Checklist: " : "Todolist: "}
        </span>
        <span className="list-progress">
          {completedCount} / {totalCount} completed
        </span>
      </div>

      <div className="list-add-item">
        <FlowTextInput
          ref={newItemInputRef}
          placeholder="Add new item..."
          value={newItemText}
          onChange={setNewItemText}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleAddItem();
            }
          }}
          expandable={false}
          icon="add"
        />
        <button
          className={`todo-action-btn ${addToTop ? "active" : ""}`}
          onClick={() => handleToggleAddPosition(true)}
          onMouseEnter={(e) => showToolTip(e, "Add items to top of list")}
        >
          <span className="material-symbols-outlined">vertical_align_top</span>
        </button>
        <button
          className={`todo-action-btn ${!addToTop ? "active" : ""}`}
          onClick={() => handleToggleAddPosition(false)}
          onMouseEnter={(e) => showToolTip(e, "Add items to bottom of list")}
        >
          <span className="material-symbols-outlined">
            vertical_align_bottom
          </span>
        </button>
      </div>

      <div
        className="list-items-container"
        onClick={() => newItemInputRef.current?.focus()}
      >
        {list.items.length === 0 ? (
          <div className="empty-items">No items in this list</div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis]}
          >
            <SortableContext
              items={list.items.map((item, idx) => item._id || `item-${idx}`)}
              strategy={verticalListSortingStrategy}
            >
              {list.items.map((item, index) => (
                <SortableListItem
                  key={item._id || index}
                  item={item}
                  index={index}
                  color={list.color}
                  onToggle={handleToggleItem}
                  onDelete={handleDeleteItem}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}
      </div>
    </div>
  );
}

export default ListEditorModal;
